<script setup lang="ts">
import { useIntegratorChild } from '#src/composables/useIntegratorChild'
import { useIntegratorStore } from '#src/stores/useIntegratorStore'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useIntegratorPluginOptions } from '#src/composables/useIntegratorPluginOptions'

interface IntegratorFrameProps {
  id: string
  url: string
  keepAlive?: number
}

const CLASS_INTEGRATOR_IFRAME = 'integrator-container__iframe'

const { id, url, keepAlive } = defineProps<IntegratorFrameProps>()
const containerRef = ref<HTMLElement | null>(null)
const { isLoaded, activateChild, removeChild } = useIntegratorChild(id)
const integratorStore = useIntegratorStore()
const { options } = useIntegratorPluginOptions()

let resizeObserver: ResizeObserver | null = null

const getOrCreateIframeElement = () => {
  let iframeEl = document.getElementById(id) as HTMLIFrameElement | null

  if (!iframeEl) {
    const iframeUrl = new URL(url)
    iframeUrl.searchParams.set('v', Date.now().toString())
    iframeUrl.searchParams.set('integratorType', options.type || 'unknown')
    iframeUrl.searchParams.set('debug', integratorStore.isDebug.toString())

    iframeEl = document.createElement('iframe')
    iframeEl.id = id
    iframeEl.src = iframeUrl.toString()
    iframeEl.setAttribute('data-testid', `integrator-iframe-${id}`)
    iframeEl.setAttribute('allow', 'local-network-access')
    iframeEl.classList.add(CLASS_INTEGRATOR_IFRAME)
    iframeEl.style.border = 'none'

    integratorStore.registerIframe(id)
  }

  return iframeEl
}

const positionIframeOverContainer = (iframeEl: HTMLIFrameElement) => {
  if (!containerRef.value) return

  resizeObserver = new ResizeObserver(() => {
    if (!containerRef.value) return

    const rect = containerRef.value.getBoundingClientRect()

    iframeEl.style.position = 'fixed'
    iframeEl.style.left = `${rect.left}px`
    iframeEl.style.top = `${rect.top}px`
    iframeEl.style.height = `${rect.height}px`
    iframeEl.style.width = `${rect.width}px`
    iframeEl.style.visibility = 'visible'
  })

  resizeObserver.observe(containerRef.value)
}

const showKeepAliveIframe = () => {
  const iframeEl = getOrCreateIframeElement()

  if (!iframeEl.parentElement) {
    iframeEl.style.visibility = 'hidden'
    document.body.appendChild(iframeEl)
  }

  positionIframeOverContainer(iframeEl)
}

const hideKeepAliveIframe = () => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  const iframeEl = document.getElementById(id) as HTMLIFrameElement | null

  if (iframeEl) {
    iframeEl.style.visibility = 'hidden'
  }
}

const scheduleIframeRemoval = () => {
  if (keepAlive === Infinity) return

  const timeoutId = window.setTimeout(() => {
    document.getElementById(id)?.remove()
    integratorStore.unregisterIframe(id)
    integratorStore.clearKeepAliveTimeout(id)
    removeChild()
  }, keepAlive)

  integratorStore.setKeepAliveTimeout(id, timeoutId)
}

const showInlineIframe = () => {
  const iframeEl = getOrCreateIframeElement()
  containerRef.value?.appendChild(iframeEl)
}

const removeInlineIframe = () => {
  integratorStore.unregisterIframe(id)
  removeChild()
}

onMounted(() => {
  const isExistingIframe = !!document.getElementById(id)

  if (keepAlive !== undefined) {
    integratorStore.clearKeepAliveTimeout(id)
    showKeepAliveIframe()
  } else {
    showInlineIframe()
  }

  if (isExistingIframe) {
    activateChild()
  }
})

onBeforeUnmount(() => {
  if (keepAlive !== undefined) {
    hideKeepAliveIframe()
    scheduleIframeRemoval()
  } else {
    removeInlineIframe()
  }
})
</script>

<template>
  <div ref="containerRef" class="integrator-container" data-testid="integrator-container">
    <div v-if="!isLoaded" class="integrator-container__loader" data-testid="integrator-loader">
      <slot :isLoading="!isLoaded" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.integrator-container {
  width: 100%;
  height: 100%;

  &__loader {
    display: grid;
    place-items: center;
    height: inherit;
  }
}

:deep(.integrator-container__iframe) {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
